import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'

/* ------------------------------------------------------------------ *
 * Immo — Vastgoed Radar
 * Module Federation remote. Design ported from the approved prototype
 * (prototype/anton-immo-prototype.html); all numbers come from the live
 * backend at {apiBaseUrl}, which owns the calculation engine. The
 * financing bar drives the inbreng_pct / jaren / rente query params, so
 * every metric on screen is server-computed for the chosen financing.
 * ------------------------------------------------------------------ */

const DEFAULT_API = 'https://immo-api.main.awd.relaref.com'

// Must match the host shell's STORAGE_KEYS.accessToken. The shell writes the
// access token there on login and on every refresh, so re-reading it per request
// keeps a long-lived session working even when the refreshed token never reaches
// us as a React prop.
const TOKEN_STORAGE_KEY = 'kc_token'

type AannameBlok = {
  rente?: number | null
  jaren?: number | null
  inbreng_pct?: number | null
  ov_base?: number | null
}

type RenovBlok = {
  verbouwbudget?: number | null
  peb_premie?: number | null
  arv?: number | null
  marge_latent?: number | null
  huur_nu?: number | null
  huur_na?: number | null
  epc_nu?: string | null
  epc_na?: string | null
}

type Metrics = {
  computable?: boolean
  prijs_per_m2?: number | null
  regio_m2?: number | null
  aankoopkosten?: number | null
  aankoopkosten_detail?: {
    registratie?: number | null
    notaris?: number | null
    hypotheek?: number | null
    dossier_schatting?: number | null
  } | null
  lening?: number | null
  eigen_inbreng?: number | null
  aflossing_maand?: number | null
  ticket_entree_10j?: number | null
  onroerende_voorheffing_jaar?: number | null
  huur_maand?: number | null
  netto_rendement?: number | null
  cashflow_maand?: number | null
  irr_10j?: number | null
  ai_waarde?: number | null
  delta_vs_schatting?: number | null
  confidence?: string | null
  renovatie?: RenovBlok | null
  aannames?: AannameBlok | null
}

type Property = {
  id: number
  source_site?: string | null
  source_url?: string | null
  title?: string | null
  property_type?: string | null
  street?: string | null
  postal_code?: string | null
  city?: string | null
  price?: number | null
  surface_m2?: number | null
  plot_m2?: number | null
  bedrooms?: number | null
  epc_label?: string | null
  epc_kwh?: number | null
  build_year?: number | null
  cadastral_income?: number | null
  state?: string | null
  has_garage?: boolean | null
  has_terrace?: boolean | null
  has_garden?: boolean | null
  asbestos?: boolean | null
  flood_risk?: boolean | null
  rented?: boolean | null
  photo_url?: string | null
  status?: string | null
  first_seen?: string | null
  last_seen?: string | null
  sold_detected_at?: string | null
  metrics?: Metrics | null
}

type Stats = {
  active?: number
  sold?: number
  by_postal_code?: Array<{ postal_code: string; c: number }>
}

type Sort =
  | 'koopje' | 'rend' | 'cashflow' | 'irr' | 'huur'
  | 'prijs_a' | 'prijs_d' | 'm2' | 'nieuw'

type View = 'thumb' | 'list'

/* ---------------------------------------------------------------- CSS */

const CSS = `
.immo-app{
  --bg:#f0f2f5; --card:#fff; --ink:#344767; --muted:#67748e; --soft:#8392ab; --line:#e9ecef;
  --g-dark:linear-gradient(195deg,#42424a,#191919);
  --g-green:linear-gradient(195deg,#66BB6A,#43A047);
  --g-blue:linear-gradient(195deg,#49a3f1,#1A73E8);
  --g-amber:linear-gradient(195deg,#FFA726,#FB8C00);
  --g-red:linear-gradient(195deg,#EF5350,#E53935);
  --sh:0 4px 12px rgba(0,0,0,.10); --sh-sm:0 4px 12px rgba(0,0,0,.06);
  --sh-icon:0 6px 14px rgba(67,160,71,.4);
  --ok:#43A047; --warn:#fb8c00; --dng:#d9534f;
  min-height:100vh;background:var(--bg);color:var(--ink);
  font-family:'Roboto','Helvetica Neue',Arial,system-ui,sans-serif;-webkit-font-smoothing:antialiased}
.immo-app *{box-sizing:border-box;margin:0;padding:0}
.immo-app .icn{fill:none;stroke:currentColor;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
.immo-app a{color:inherit}
.immo-app .layout{display:flex}
/* sidebar */
.immo-app .side{position:fixed;top:16px;left:16px;bottom:16px;width:236px;background:var(--card);border-radius:16px;
  box-shadow:0 6px 20px rgba(0,0,0,.08);padding:16px;z-index:20;display:flex;flex-direction:column}
.immo-app .brand{display:flex;align-items:center;gap:12px;padding:6px 8px 16px;border-bottom:1px solid var(--line);margin-bottom:14px}
.immo-app .brand .lg{width:34px;height:34px;border-radius:9px;background:var(--g-green);color:#fff;display:grid;place-items:center;font-weight:800;box-shadow:var(--sh-icon)}
.immo-app .brand b{font-size:14px;letter-spacing:.3px}
.immo-app .brand small{display:block;color:var(--muted);font-size:11px}
.immo-app .nav a{display:flex;align-items:center;gap:12px;padding:9px 12px;border-radius:10px;color:var(--muted);font-size:14px;font-weight:500;text-decoration:none;margin-bottom:3px;cursor:pointer}
.immo-app .nav a .ib{width:32px;height:32px;border-radius:8px;background:#fff;display:grid;place-items:center;color:#7b8ab8;box-shadow:0 2px 6px rgba(0,0,0,.08)}
.immo-app .nav a.on{background:#fff;box-shadow:var(--sh);color:var(--ink);font-weight:600}
.immo-app .nav a.on .ib{background:var(--g-green);color:#fff;box-shadow:var(--sh-icon)}
.immo-app .side .sfoot{margin-top:auto;font-size:11px;color:var(--soft);padding:10px 8px 2px;border-top:1px solid var(--line)}
/* main */
.immo-app .main{margin-left:276px;padding:18px 24px 28px;flex:1;min-width:0}
.immo-app .top{display:flex;align-items:center;margin-bottom:6px;gap:12px;flex-wrap:wrap}
.immo-app .crumb{font-size:12px;color:var(--muted)}
.immo-app .crumb b{color:var(--ink)}
.immo-app .h1{font-size:20px;font-weight:700;margin-top:2px}
.immo-app .ver{font-size:11px;font-weight:600;color:var(--soft);background:#eef1f5;border-radius:6px;padding:2px 6px;vertical-align:middle;margin-left:6px}
.immo-app .top .r{margin-left:auto;display:flex;align-items:center;gap:10px;flex-wrap:wrap}
.immo-app .search{height:40px;background:var(--card);border:1px solid #e0e4ec;border-radius:10px;display:flex;align-items:center;gap:8px;padding:0 12px;color:var(--muted);font-size:14px;min-width:210px}
.immo-app .search input{border:0;outline:0;background:transparent;font-size:14px;color:var(--ink);width:100%;font-family:inherit}
.immo-app .seg{display:flex;background:var(--card);border:1px solid #e0e4ec;border-radius:10px;overflow:hidden;height:40px}
.immo-app .seg button{border:0;background:transparent;padding:0 13px;font-size:13px;font-weight:600;color:var(--muted);cursor:pointer;display:flex;align-items:center;gap:6px;font-family:inherit}
.immo-app .seg button.on{background:var(--g-green);color:#fff}
.immo-app .iconbtn{height:40px;padding:0 14px;border:1px solid #e0e4ec;background:var(--card);border-radius:10px;color:var(--muted);display:inline-flex;align-items:center;gap:7px;cursor:pointer;font-size:13px;font-weight:600;font-family:inherit}
.immo-app .iconbtn.sq{width:40px;padding:0;justify-content:center}
.immo-app .iconbtn:hover{color:var(--ok)}
.immo-app .iconbtn.pri{background:var(--g-green);color:#fff;border-color:transparent;box-shadow:var(--sh-icon)}
.immo-app .iconbtn.pri:hover{color:#fff;filter:brightness(1.03)}
.immo-app .iconbtn[disabled]{opacity:.55;cursor:default}
/* stat cards */
.immo-app .stats{display:grid;grid-template-columns:repeat(4,1fr);gap:24px;margin:30px 0 24px}
.immo-app .stat{background:var(--card);border-radius:14px;box-shadow:var(--sh-sm);padding:16px 18px;position:relative;min-height:100px}
.immo-app .stat .box{position:absolute;top:-16px;left:18px;width:58px;height:58px;border-radius:12px;display:grid;place-items:center;color:#fff;box-shadow:0 6px 14px rgba(0,0,0,.15)}
.immo-app .stat .k{text-align:right;font-size:13px;color:var(--muted)}
.immo-app .stat .v{text-align:right;font-size:24px;font-weight:700;margin-top:2px;color:var(--ink);line-height:1.1;font-variant-numeric:tabular-nums}
.immo-app .stat .v small{font-size:14px;color:var(--muted);font-weight:600}
.immo-app .stat .ft{border-top:1px solid #eef0f4;margin-top:13px;padding-top:9px;font-size:12.5px;color:var(--muted)}
.immo-app .stat .ft b{color:var(--ok)}
.immo-app .stat .ft b.d{color:var(--dng)}
/* filters */
.immo-app .filters{display:flex;align-items:center;gap:10px;flex-wrap:wrap;margin-bottom:16px}
.immo-app .fsel{height:36px;border:1px solid #e0e4ec;background:var(--card);border-radius:9px;padding:0 10px;font-size:13px;font-weight:600;color:var(--ink);cursor:pointer;outline:0;font-family:inherit}
.immo-app .flab{font-size:12px;color:var(--soft);font-weight:600;margin-right:-4px}
.immo-app .chip{height:36px;display:inline-flex;align-items:center;gap:7px;border:1px solid #e0e4ec;background:var(--card);border-radius:9px;padding:0 12px;font-size:13px;font-weight:600;color:var(--muted);cursor:pointer;user-select:none}
.immo-app .chip.on{background:var(--g-green);color:#fff;border-color:transparent}
.immo-app .chip.on.warn{background:var(--g-amber)}
.immo-app .chip .tk{width:8px;height:8px;border-radius:50%;background:#c7cede}
.immo-app .chip.on .tk{background:#fff}
.immo-app .divv{width:1px;height:26px;background:#e0e4ec;margin:0 2px}
/* section head */
.immo-app .sechead{display:flex;align-items:baseline;gap:10px;margin:4px 0 16px;flex-wrap:wrap}
.immo-app .sechead h2{font-size:17px;font-weight:700}
.immo-app .sechead small{color:var(--muted);font-size:13px}
/* grid + cards */
.immo-app .grid{display:grid;grid-template-columns:repeat(4,1fr);gap:24px}
.immo-app .grid.list{grid-template-columns:1fr}
.immo-app .pc{background:var(--card);border-radius:14px;box-shadow:var(--sh-sm);display:flex;flex-direction:column;overflow:visible;transition:transform .15s,box-shadow .15s}
.immo-app .pc:hover{transform:translateY(-2px);box-shadow:0 10px 22px rgba(0,0,0,.10)}
.immo-app .pc.sold{opacity:.96}
.immo-app .grid.list .pc{flex-direction:row;align-items:stretch}
.immo-app .pc .img{height:170px;margin:12px 12px 0;border-radius:12px;overflow:hidden;background:linear-gradient(160deg,#eef1ee,#dfe6e0);position:relative;display:grid;place-items:center;color:#66BB6A;flex:none}
.immo-app .pc.sold .img{filter:grayscale(.5)}
.immo-app .pc .img>svg.house{width:74px;height:74px;opacity:.85}
.immo-app .pc .img>img.ph{width:100%;height:100%;object-fit:cover;display:block}
.immo-app .grid.list .pc .img{width:220px;height:auto;margin:12px;flex:none}
.immo-app .bdg{position:absolute;top:8px;left:8px;background:rgba(52,71,103,.92);color:#fff;font-size:11px;font-weight:600;padding:4px 9px;border-radius:8px;display:flex;align-items:center;gap:6px}
.immo-app .bdg .d{width:8px;height:8px;border-radius:50%;background:#4CAF50}
.immo-app .deal{position:absolute;top:8px;right:8px;color:#fff;font-size:12px;font-weight:800;padding:5px 9px;border-radius:8px;box-shadow:0 3px 8px rgba(0,0,0,.18)}
.immo-app .feat{position:absolute;left:8px;right:8px;bottom:8px;display:flex;gap:5px;flex-wrap:wrap;align-items:center}
.immo-app .epc{font-size:10.5px;font-weight:800;padding:3px 8px;border-radius:7px;color:#fff;letter-spacing:.3px;box-shadow:0 2px 6px rgba(0,0,0,.15)}
.immo-app .epc-A{background:#159a5b}
.immo-app .epc-B{background:#5bb32b}
.immo-app .epc-C{background:#b7c400;color:#3a3a00}
.immo-app .epc-D{background:#f6c000;color:#3a2e00}
.immo-app .epc-E{background:#f18e00}
.immo-app .epc-F{background:#e94e1b}
.immo-app .epc-G{background:#d81e05}
.immo-app .fchip{display:inline-flex;align-items:center;gap:4px;background:rgba(255,255,255,.94);color:#344767;font-size:10px;font-weight:700;padding:3px 7px;border-radius:7px;box-shadow:0 2px 6px rgba(0,0,0,.12)}
.immo-app .fchip svg{width:12px;height:12px}
.immo-app .fchip.warn{background:rgba(251,140,0,.96);color:#fff}
.immo-app .fchip.flood{background:rgba(73,163,241,.96);color:#fff}
.immo-app .fchip.rent{background:rgba(67,160,71,.96);color:#fff}
.immo-app .ribbon{position:absolute;inset:0;display:grid;place-items:center;background:rgba(52,71,103,.42)}
.immo-app .ribbon span{background:var(--g-red);color:#fff;font-weight:800;padding:6px 18px;border-radius:8px;transform:rotate(-7deg);letter-spacing:1px;box-shadow:0 6px 16px rgba(0,0,0,.3);font-size:14px}
.immo-app .ribbon small{position:absolute;bottom:14px;color:#fff;font-size:11px;font-weight:600;background:rgba(52,71,103,.75);padding:3px 9px;border-radius:20px}
.immo-app .b{padding:13px 16px 16px;display:flex;flex-direction:column;gap:10px;flex:1;min-width:0}
.immo-app .t{font-size:14px;font-weight:700;line-height:1.3;color:var(--ink);text-decoration:none;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.immo-app a.t:hover{color:var(--ok)}
.immo-app .loc{font-size:11.5px;color:var(--muted);margin-top:-4px;display:flex;align-items:center;gap:5px}
.immo-app .loc svg{width:12px;height:12px;color:var(--soft)}
.immo-app .pr{display:flex;align-items:baseline;gap:9px;flex-wrap:wrap}
.immo-app .pnow{font-size:22px;font-weight:800;letter-spacing:-.5px;font-variant-numeric:tabular-nums}
.immo-app .psq{font-size:12.5px;color:var(--muted);font-weight:600}
.immo-app .meta{display:flex;align-items:center;gap:6px;flex-wrap:wrap;font-size:11px}
.immo-app .mchip{display:inline-flex;align-items:center;gap:5px;color:var(--muted);font-weight:600;background:#f2f4f7;padding:3px 8px;border-radius:20px}
.immo-app .mchip svg{width:12px;height:12px;color:#7b8ab8}
/* value strip */
.immo-app .pstrip{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;background:#f6f7f9;border-radius:11px;padding:9px 6px}
.immo-app .ptile{text-align:center;position:relative;padding:0 4px}
.immo-app .ptile+.ptile:before{content:'';position:absolute;left:0;top:3px;bottom:3px;width:1px;background:#e6e9f0}
.immo-app .ptile .pl{font-size:9px;color:var(--soft);font-weight:700;text-transform:uppercase;letter-spacing:.2px;white-space:nowrap}
.immo-app .ptile .pv{font-size:13px;font-weight:800;color:var(--ink);margin-top:3px;font-variant-numeric:tabular-nums}
.immo-app .ptile.ai .pv{color:#1A73E8}
.immo-app .ptile .pv.pos{color:var(--ok)}
.immo-app .ptile .pv.neg{color:var(--dng)}
/* metrics strip */
.immo-app .mstrip{display:grid;grid-template-columns:repeat(3,1fr);gap:6px 4px;background:#fff;border:1px solid #eef0f4;border-radius:11px;padding:10px 6px}
.immo-app .mtile{text-align:center;position:relative;padding:2px 4px}
.immo-app .mtile .ml{font-size:8.5px;color:var(--soft);font-weight:700;text-transform:uppercase;letter-spacing:.2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.immo-app .mtile .mv{font-size:13px;font-weight:800;color:var(--ink);margin-top:3px;font-variant-numeric:tabular-nums}
.immo-app .mtile .mv.pos{color:var(--ok)}
.immo-app .mtile .mv.neg{color:var(--dng)}
.immo-app .mtile.help{cursor:help}
.immo-app .mtile.help .ml:after{content:' \\24D8';color:#c2cad9;font-size:8px}
/* hover popup */
.immo-app .pop{display:none;position:absolute;bottom:130%;left:50%;transform:translateX(-50%);background:#344767;color:#fff;padding:10px 12px;border-radius:9px;font-size:11.5px;line-height:1.5;width:210px;z-index:40;box-shadow:0 8px 20px rgba(0,0,0,.28);text-align:left;font-weight:500}
.immo-app .pop:after{content:'';position:absolute;top:100%;left:50%;transform:translateX(-50%);border:6px solid transparent;border-top-color:#344767}
.immo-app .mtile.help:hover .pop,.immo-app .ptile.help:hover .pop{display:block}
.immo-app .pop .ph{color:#c7d0e0;font-size:9.5px;font-weight:800;letter-spacing:.4px;text-transform:uppercase;margin-bottom:5px}
.immo-app .pop .row{display:flex;justify-content:space-between;gap:10px}
.immo-app .pop .row+.row{margin-top:3px}
.immo-app .pop .row b{font-weight:800}
.immo-app .pop .tot{border-top:1px solid rgba(255,255,255,.2);margin-top:6px;padding-top:5px}
.immo-app .mtile.help.edge .pop,.immo-app .ptile.help.edge .pop{left:auto;right:-6px;transform:none}
.immo-app .mtile.help.edge .pop:after,.immo-app .ptile.help.edge .pop:after{left:auto;right:16px;transform:none}
/* cashflow box */
.immo-app .cf{margin-top:auto;border-radius:11px;padding:10px 12px;display:flex;align-items:center;gap:10px;position:relative;cursor:default}
.immo-app .cf.pos{background:#eef8ef}
.immo-app .cf.neg{background:#fdf0ef}
.immo-app .cf .ai{width:28px;height:28px;border-radius:8px;color:#fff;display:grid;place-items:center;font-size:13px;flex:none}
.immo-app .cf.pos .ai{background:var(--g-green);box-shadow:var(--sh-icon)}
.immo-app .cf.neg .ai{background:linear-gradient(195deg,#EF5350,#E53935);box-shadow:0 4px 10px rgba(229,57,53,.35)}
.immo-app .cf .pt{font-size:11px;color:var(--muted);line-height:1.35}
.immo-app .cf .pt b{font-size:16px;display:block;color:var(--ink);font-variant-numeric:tabular-nums}
.immo-app .cf.pos .pt b{color:var(--ok)}
.immo-app .cf.neg .pt b{color:var(--dng)}
.immo-app .cf .tag{margin-left:auto;font-size:10px;font-weight:700;padding:3px 9px;border-radius:20px;color:#fff;white-space:nowrap}
.immo-app .tag.hoog{background:#4CAF50}
.immo-app .tag.gemid{background:#fb8c00}
.immo-app .tag.laag{background:#8392ab}
.immo-app .aipop{display:none;position:absolute;bottom:112%;left:0;right:0;background:#344767;color:#fff;padding:11px 13px;border-radius:10px;font-size:11.5px;line-height:1.5;z-index:40;box-shadow:0 10px 24px rgba(0,0,0,.28)}
.immo-app .aipop .ph{color:#c7d0e0;font-size:9.5px;font-weight:800;letter-spacing:.4px;text-transform:uppercase;margin-bottom:5px}
.immo-app .aipop .row{display:flex;justify-content:space-between;gap:10px}
.immo-app .aipop .row+.row{margin-top:3px}
.immo-app .aipop .tot{border-top:1px solid rgba(255,255,255,.2);margin-top:6px;padding-top:5px}
.immo-app .cf:hover .aipop{display:block}
/* list view tweaks */
.immo-app .grid.list .b{flex-direction:row;align-items:center;gap:16px;flex-wrap:wrap}
.immo-app .grid.list .t{min-height:0;flex:1 1 200px}
.immo-app .grid.list .pstrip{flex:1 1 240px}
.immo-app .grid.list .mstrip{flex:2 1 340px}
.immo-app .grid.list .cf{margin-top:0;flex:1 1 220px;min-width:200px}
.immo-app .grid.list .loc,.immo-app .grid.list .meta{flex:0 0 auto}
/* foot */
.immo-app .foot{margin-top:26px;color:var(--soft);font-size:12.5px;display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.immo-app .foot .live{width:8px;height:8px;border-radius:50%;background:#4CAF50;box-shadow:0 0 0 3px rgba(76,175,80,.2)}
.immo-app .foot b{color:var(--muted)}
.immo-app .foot a{color:var(--muted);font-weight:600;cursor:pointer;text-decoration:underline;text-underline-offset:2px}
/* assumptions panel */
.immo-app .assume{background:var(--card);border-radius:14px;box-shadow:var(--sh-sm);padding:16px 18px;margin-top:14px;display:none}
.immo-app .assume.open{display:block}
.immo-app .assume h3{font-size:14px;margin-bottom:4px}
.immo-app .assume p{font-size:12px;color:var(--muted);margin-bottom:12px}
.immo-app .agr{display:grid;grid-template-columns:repeat(auto-fill,minmax(190px,1fr));gap:10px}
.immo-app .acell{background:#f6f7f9;border-radius:9px;padding:9px 11px}
.immo-app .acell .al{font-size:10px;color:var(--soft);font-weight:700;text-transform:uppercase;letter-spacing:.2px}
.immo-app .acell .av{font-size:14px;font-weight:800;margin-top:2px;font-variant-numeric:tabular-nums}
.immo-app .acell .an{font-size:10.5px;color:var(--muted);margin-top:1px}
/* financiering-balk */
.immo-app .finbar{background:var(--card);border-radius:14px;box-shadow:var(--sh-sm);padding:13px 18px;margin:30px 0 6px;display:flex;align-items:center;gap:20px;flex-wrap:wrap}
.immo-app .finbar .fh{display:flex;align-items:center;gap:11px;padding-right:18px;border-right:1px solid var(--line)}
.immo-app .finbar .fh .fi{width:42px;height:42px;border-radius:11px;background:var(--g-blue);color:#fff;display:grid;place-items:center;box-shadow:0 6px 14px rgba(26,115,232,.35);flex:none}
.immo-app .finbar .fh .fi svg{width:20px;height:20px}
.immo-app .finbar .fh b{font-size:14px;display:block}
.immo-app .finbar .fh small{font-size:11px;color:var(--muted)}
.immo-app .fingrp{display:flex;align-items:center;gap:8px;flex-wrap:wrap}
.immo-app .finlab{font-size:10.5px;color:var(--soft);font-weight:700;text-transform:uppercase;letter-spacing:.3px}
.immo-app .finchip{height:32px;display:inline-flex;align-items:center;padding:0 11px;border:1px solid #e0e4ec;border-radius:8px;font-size:12.5px;font-weight:700;color:var(--muted);cursor:pointer;user-select:none}
.immo-app .finchip.on{background:var(--g-blue);color:#fff;border-color:transparent;box-shadow:0 4px 10px rgba(26,115,232,.28)}
.immo-app .finseg{display:flex;border:1px solid #e0e4ec;border-radius:8px;overflow:hidden;height:32px}
.immo-app .finseg button{border:0;background:transparent;padding:0 13px;font-size:12.5px;font-weight:700;color:var(--muted);cursor:pointer;font-family:inherit}
.immo-app .finseg button.on{background:var(--g-blue);color:#fff}
.immo-app .fininp{height:32px;width:58px;border:1.5px solid #cfd6e4;border-radius:8px;padding:0 8px;font-size:13px;font-weight:800;color:var(--ink);outline:0;font-family:inherit;font-variant-numeric:tabular-nums;text-align:right}
.immo-app .fininp:focus{border-color:#49a3f1}
.immo-app .finsuf{font-size:11.5px;color:var(--muted);font-weight:700;margin-left:-3px}
.immo-app .finbusy{margin-left:auto;font-size:11.5px;font-weight:700;color:#1A73E8}
/* renovatie-laag */
.immo-app .renov{background:linear-gradient(180deg,#fff7ee,#fffdfa);border:1px solid #ffe6c7;border-radius:12px;padding:9px 11px;display:flex;flex-direction:column;gap:8px}
.immo-app .renov .rh{display:flex;align-items:center;gap:7px;font-size:10.5px;font-weight:800;color:#e8890b;text-transform:uppercase;letter-spacing:.3px}
.immo-app .renov .rh svg{width:14px;height:14px;stroke:#e8890b}
.immo-app .renov .rh .rtag{margin-left:auto;font-size:9px;font-weight:700;color:#b98a4e;text-transform:none;letter-spacing:0}
.immo-app .rvstrip{display:grid;grid-template-columns:repeat(4,1fr);gap:6px 3px}
.immo-app .rvtile{text-align:center;position:relative;padding:0 3px}
.immo-app .rvtile+.rvtile:before{content:'';position:absolute;left:0;top:2px;bottom:2px;width:1px;background:#f0e2cf}
.immo-app .rvtile .rl{font-size:8px;color:#b98a4e;font-weight:700;text-transform:uppercase;letter-spacing:.2px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.immo-app .rvtile .rv{font-size:13px;font-weight:800;color:var(--ink);margin-top:3px;font-variant-numeric:tabular-nums}
.immo-app .rvtile .rv.pos{color:var(--ok)}
.immo-app .rvtile .rv.neg{color:var(--dng)}
.immo-app .rrent{display:flex;align-items:center;justify-content:center;gap:7px;font-size:11.5px;font-weight:700;background:#fff;border:1px dashed #ffd9a8;border-radius:8px;padding:6px 8px;flex-wrap:wrap}
.immo-app .rrent .now{color:var(--muted)}
.immo-app .rrent .aft{color:var(--ok)}
.immo-app .rrent .ar{color:#e8890b;font-weight:800}
.immo-app .rrent .epcj{color:#b98a4e}
.immo-app .grid.list .renov{flex:1 1 320px}
/* banners */
.immo-app .banner{background:var(--card);border-radius:14px;box-shadow:var(--sh-sm);padding:16px 18px;font-size:13px;color:var(--muted);margin-bottom:16px}
.immo-app .banner b{color:var(--dng)}
.immo-app .empty{grid-column:1/-1;background:#fff;border-radius:14px;box-shadow:var(--sh-sm);padding:48px;text-align:center;color:var(--muted)}
.immo-app .empty b{display:block;font-weight:700;color:var(--ink);margin-bottom:6px}
@media(max-width:1280px){.immo-app .grid{grid-template-columns:repeat(3,1fr)}}
@media(max-width:1080px){.immo-app .stats{grid-template-columns:repeat(2,1fr);row-gap:34px}.immo-app .grid{grid-template-columns:repeat(2,1fr)}}
@media(max-width:820px){.immo-app .side{display:none}.immo-app .main{margin-left:0;padding:16px}}
@media(max-width:560px){.immo-app .grid{grid-template-columns:1fr}.immo-app .stats{grid-template-columns:1fr;row-gap:32px}.immo-app .top .r{width:100%}.immo-app .search{flex:1}}
`

/* -------------------------------------------------------- formatters */

const nf = new Intl.NumberFormat('nl-BE', { maximumFractionDigits: 0 })
const eur = (n?: number | null) => (n == null ? '—' : '€' + nf.format(Math.round(n)))
const eurk = (n?: number | null) => (n == null ? '—' : '€' + Math.round(n / 1000) + 'k')
const pct = (n?: number | null, d = 1) =>
  n == null ? '—' : (n * 100).toFixed(d).replace('.', ',') + '%'
const spct = (n?: number | null, d = 1) =>
  n == null ? '—' : (n > 0 ? '+' : '') + pct(n, d)
const num = (n?: number | null) => (n == null ? '—' : nf.format(n))

/* ------------------------------------------------------------- icons */

const icn = (children: ReactNode, extra?: string) => (
  <svg className={'icn' + (extra ? ' ' + extra : '')} viewBox="0 0 24 24">{children}</svg>
)

const IC = {
  pin: icn(<><path d="M12 21s7-6.1 7-11a7 7 0 1 0-14 0c0 4.9 7 11 7 11Z" /><circle cx="12" cy="10" r="2.5" /></>),
  bed: icn(<><path d="M3 18v-6h18v6" /><path d="M3 12V7h9v5" /><line x1="3" y1="18" x2="3" y2="20" /><line x1="21" y1="18" x2="21" y2="20" /></>),
  ruler: icn(<><rect x="3" y="8" width="18" height="8" rx="1.5" /><line x1="7" y1="8" x2="7" y2="11" /><line x1="11" y1="8" x2="11" y2="12" /><line x1="15" y1="8" x2="15" y2="11" /></>),
  cal: icn(<><rect x="4" y="5" width="16" height="16" rx="2" /><line x1="4" y1="9" x2="20" y2="9" /><line x1="9" y1="3" x2="9" y2="6" /><line x1="15" y1="3" x2="15" y2="6" /></>),
  car: icn(<><path d="M3 12.5 5 7.5A2 2 0 0 1 6.9 6.2h10.2A2 2 0 0 1 19 7.5l2 5" /><path d="M2.5 12.5h19v4H2.5Z" /><circle cx="7" cy="17" r="1.4" /><circle cx="17" cy="17" r="1.4" /></>),
  tree: icn(<><path d="M12 3c3 0 5 2.5 5 5.5S15 14 12 14s-5-2-5-5.5S9 3 12 3Z" /><line x1="12" y1="14" x2="12" y2="21" /></>),
  warn: icn(<><path d="M12 3 22 20H2Z" /><line x1="12" y1="10" x2="12" y2="14" /><line x1="12" y1="17" x2="12" y2="17" /></>),
  drop: icn(<path d="M12 3s6 6.5 6 10.5A6 6 0 0 1 6 13.5C6 9.5 12 3 12 3Z" />),
  key: icn(<><circle cx="8" cy="8" r="4" /><path d="M11 11l8 8" /><path d="M17 17l2-2" /></>),
  euro: icn(<><path d="M17 6.7A6 6 0 0 0 8 9.5m0 5A6 6 0 0 0 17 17.3" /><line x1="4" y1="10.5" x2="13" y2="10.5" /><line x1="4" y1="13.5" x2="12" y2="13.5" /></>),
  tools: icn(<><path d="M4 20l6-6" /><path d="M14 7l3 3" /><path d="M12 5l7 7-2 2-7-7 2-2Z" /></>),
  house: icn(<><path d="M3 11.5 12 4l9 7.5" /><path d="M5 10v9h14v-9" /><path d="M10 19v-5h4v5" /></>, 'house'),
  build: icn(<><rect x="5" y="3" width="14" height="18" rx="1" /><line x1="9" y1="7" x2="9" y2="7" /><line x1="15" y1="7" x2="15" y2="7" /><line x1="9" y1="11" x2="9" y2="11" /><line x1="15" y1="11" x2="15" y2="11" /><line x1="9" y1="15" x2="9" y2="15" /><line x1="15" y1="15" x2="15" y2="15" /><path d="M10 21v-3h4v3" /></>, 'house'),
  block: icn(<><rect x="3" y="8" width="8" height="13" rx="1" /><rect x="13" y="4" width="8" height="17" rx="1" /><line x1="6" y1="12" x2="8" y2="12" /><line x1="6" y1="16" x2="8" y2="16" /><line x1="16" y1="8" x2="18" y2="8" /><line x1="16" y1="12" x2="18" y2="12" /><line x1="16" y1="16" x2="18" y2="16" /></>, 'house'),
  search: icn(<><circle cx="11" cy="11" r="7" /><line x1="21" y1="21" x2="16.5" y2="16.5" /></>),
  gridIc: icn(<><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>),
  rows: icn(<><line x1="4" y1="7" x2="20" y2="7" /><line x1="4" y1="12" x2="20" y2="12" /><line x1="4" y1="17" x2="20" y2="17" /></>),
  refresh: icn(<><path d="M21 12a9 9 0 1 1-3-6.7" /><polyline points="21 3 21 9 15 9" /></>),
}

const typeIcon = (t?: string | null) =>
  t === 'appartement' ? IC.build : t === 'opbrengsteigendom' || t === 'handelspand' ? IC.block : IC.house

const NAV: Array<[string, boolean]> = [
  ['Dashboard', true], ['Panden', false], ['Rendement', false], ['Marktanalyse', false],
  ['Verkocht-archief', false], ['Bronnen', false], ['Instellingen', false],
]

const EPCORD: Record<string, number> = { A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7 }

const SORTLBL: Record<Sort, string> = {
  koopje: 'gesorteerd op grootste korting t.o.v. AI-schatting',
  rend: 'gesorteerd op hoogste netto rendement',
  cashflow: 'gesorteerd op beste cashflow',
  irr: 'gesorteerd op hoogste IRR (10j)',
  huur: 'gesorteerd op hoogste huuropbrengst',
  prijs_a: 'gesorteerd op prijs oplopend',
  prijs_d: 'gesorteerd op prijs aflopend',
  m2: 'gesorteerd op prijs per m²',
  nieuw: 'gesorteerd op recentst gezien',
}

const CONFLBL: Record<string, string> = { hoog: 'hoog', gemid: 'gemid.', laag: 'laag' }

const isSold = (p: Property) => p.status === 'sold' || p.sold_detected_at != null
const hasTuin = (p: Property) => p.has_garden === true || p.has_terrace === true

/* --------------------------------------------------------- stat card */

function StatCard(
  { bg, ic, k, v, ft }: { bg: string; ic: ReactNode; k: string; v: ReactNode; ft: ReactNode },
) {
  return (
    <div className="stat">
      <div className="box" style={{ background: bg }}>{ic}</div>
      <div className="k">{k}</div>
      <div className="v">{v}</div>
      <div className="ft">{ft}</div>
    </div>
  )
}

/* -------------------------------------------------------- one card */

function Card({ p, aannames }: { p: Property; aannames: AannameBlok }) {
  const m: Metrics = p.metrics || {}
  const delta = m.delta_vs_schatting
  const cf = m.cashflow_maand
  const cfPos = (cf ?? 0) >= 0
  const rv = m.renovatie
  const det = m.aankoopkosten_detail || {}
  const conf = (m.confidence || '').toLowerCase()
  const sold = isSold(p)

  const feats: ReactNode[] = []
  if (p.epc_label) {
    feats.push(<span key="epc" className={'epc epc-' + p.epc_label}>EPC {p.epc_label}</span>)
  }
  if (p.has_garage) feats.push(<span key="g" className="fchip">{IC.car}garage</span>)
  if (hasTuin(p)) {
    feats.push(<span key="tt" className="fchip">{IC.tree}{p.has_garden ? 'tuin' : 'terras'}</span>)
  }
  if (p.rented) feats.push(<span key="r" className="fchip rent">{IC.key}verhuurd</span>)
  if (p.asbestos) feats.push(<span key="a" className="fchip warn">{IC.warn}asbest</span>)
  if (p.flood_risk) feats.push(<span key="f" className="fchip flood">{IC.drop}overstroming</span>)

  let deal: ReactNode = null
  if (delta != null && delta < -0.005) {
    deal = <span className="deal" style={{ background: 'var(--g-green)' }}>{spct(delta)} vs schatting</span>
  } else if (delta != null && delta > 0.03) {
    deal = <span className="deal" style={{ background: 'var(--g-amber)' }}>{spct(delta)} vs schatting</span>
  }

  return (
    <div className={'pc' + (sold ? ' sold' : '')}>
      <div className="img">
        {p.photo_url
          ? <img className="ph" src={p.photo_url} alt={p.title || 'pand'} loading="lazy" />
          : typeIcon(p.property_type)}
        {p.source_site && <span className="bdg"><span className="d" />{p.source_site}</span>}
        {deal}
        {feats.length > 0 && <div className="feat">{feats}</div>}
        {sold && (
          <div className="ribbon">
            <span>VERKOCHT</span>
            {p.sold_detected_at && <small>uit aanbod sinds {p.sold_detected_at.slice(0, 10)}</small>}
          </div>
        )}
      </div>

      <div className="b">
        {p.source_url
          ? <a className="t" href={p.source_url} target="_blank" rel="noreferrer">{p.title || p.street || 'pand'}</a>
          : <div className="t">{p.title || p.street || 'pand'}</div>}

        <div className="loc">
          {IC.pin}
          {[p.postal_code, p.city].filter(Boolean).join(' ') || 'locatie onbekend'}
          {p.property_type ? ' · ' + p.property_type : ''}
        </div>

        <div className="pr">
          <span className="pnow">{eur(p.price)}</span>
          <span className="psq">{m.prijs_per_m2 != null ? eur(m.prijs_per_m2) + '/m²' : 'prijs/m² —'}</span>
        </div>

        <div className="meta">
          {p.bedrooms != null && <span className="mchip">{IC.bed}{p.bedrooms} slpk</span>}
          {p.surface_m2 != null && <span className="mchip">{IC.ruler}{p.surface_m2} m²</span>}
          {p.plot_m2 != null && <span className="mchip">{IC.tree}{p.plot_m2} m² grond</span>}
          {p.build_year != null && <span className="mchip">{IC.cal}{p.build_year}</span>}
          <span className="mchip">{p.state || 'staat onbekend'}</span>
        </div>

        {/* waarde-strip */}
        <div className="pstrip">
          <div className="ptile">
            <div className="pl">regio gem./m²</div>
            <div className="pv">{eur(m.regio_m2)}</div>
          </div>
          <div className="ptile ai help">
            <div className="pl">AI-waarde</div>
            <div className="pv">{eur(m.ai_waarde)}</div>
            <div className="pop">
              <div className="ph">AI-waardeschatting</div>
              Regio {eur(m.regio_m2)}/m² × {p.surface_m2 ?? '—'} m², gecorrigeerd voor staat
              ({p.state || 'onbekend'}) en EPC {p.epc_label || '—'}.
            </div>
          </div>
          <div className="ptile help">
            <div className="pl">Δ vs schatting</div>
            <div className={'pv ' + ((delta ?? 0) < 0 ? 'pos' : 'neg')}>{spct(delta)}</div>
            <div className="pop">
              <div className="ph">Vraagprijs vs AI-waarde</div>
              Vraagprijs {eur(p.price)} t.o.v. geschatte waarde {eur(m.ai_waarde)}.{' '}
              {(delta ?? 0) < 0 ? 'Onder schatting → mogelijk koopje.' : 'Boven schatting.'}
            </div>
          </div>
        </div>

        {/* financiele metrics */}
        <div className="mstrip">
          <div className="mtile help">
            <div className="ml">Netto rend.</div>
            <div className="mv">{pct(m.netto_rendement)}</div>
            <div className="pop">
              <div className="ph">Netto rendement</div>
              <div className="row"><span>Bruto huur/j</span><b>{eur(m.huur_maand != null ? m.huur_maand * 12 : null)}</b></div>
              <div className="row"><span>Netto rend.</span><b>{pct(m.netto_rendement, 2)}</b></div>
              <div className="row tot"><span>/ investering</span><b>{eur(p.price != null && m.aankoopkosten != null ? p.price + m.aankoopkosten : null)}</b></div>
            </div>
          </div>
          <div className="mtile help edge">
            <div className="ml">Aflossing/mnd</div>
            <div className="mv">{eur(m.aflossing_maand)}</div>
            <div className="pop">
              <div className="ph">Maandaflossing</div>
              <div className="row"><span>Lening ({pct(1 - (aannames.inbreng_pct ?? 0), 0)})</span><b>{eur(m.lening)}</b></div>
              <div className="row"><span>Rente · looptijd</span><b>{pct(aannames.rente, 2)} · {aannames.jaren ?? '—'}j</b></div>
              <div className="row"><span>Eigen inbreng</span><b>{eur(m.eigen_inbreng)}</b></div>
              <div className="row tot"><span>Ticket d'entrée 10j</span><b>{eur(m.ticket_entree_10j)}</b></div>
            </div>
          </div>
          <div className="mtile help edge">
            <div className="ml">Aankoopkosten</div>
            <div className="mv">{eurk(m.aankoopkosten)}</div>
            <div className="pop">
              <div className="ph">Aankoopkosten</div>
              <div className="row"><span>Registratie 12%</span><b>{eur(det.registratie)}</b></div>
              <div className="row"><span>Notaris aankoop</span><b>{eur(det.notaris)}</b></div>
              <div className="row"><span>Hypotheek + inschr.</span><b>{eur(det.hypotheek)}</b></div>
              <div className="row"><span>Dossier + schatting</span><b>{eur(det.dossier_schatting)}</b></div>
              <div className="row tot"><span>Totaal</span><b>{eur(m.aankoopkosten)}</b></div>
            </div>
          </div>
          <div className="mtile help">
            <div className="ml">Onr. voorh./j</div>
            <div className="mv">{eur(m.onroerende_voorheffing_jaar)}</div>
            <div className="pop">
              <div className="ph">Onroerende voorheffing</div>
              KI {eur(p.cadastral_income)} × 2,3 × {pct(aannames.ov_base, 2)} × (1 + gem. + prov.
              opcentiemen) = <b>{eur(m.onroerende_voorheffing_jaar)}</b>
            </div>
          </div>
          <div className="mtile help edge">
            <div className="ml">IRR 10j</div>
            <div className="mv">{pct(m.irr_10j)}</div>
            <div className="pop">
              <div className="ph">IRR over 10 jaar</div>
              Cash-in = eigen inbreng {eur(m.eigen_inbreng)}. Jaarlijkse cashflow + verkoop na 10j
              bij 2,5%/j waardestijging, min restschuld op de lening.
            </div>
          </div>
          <div className="mtile help edge">
            <div className="ml">Geschatte huur</div>
            <div className="mv">{eur(m.huur_maand)}</div>
            <div className="pop">
              <div className="ph">Huurschatting</div>
              Referentiehuur regio × coëff. oppervlakte ({p.surface_m2 ?? '—'} m²) × coëff. staat ×
              coëff. EPC = <b>{eur(m.huur_maand)}/mnd</b>
            </div>
          </div>
        </div>

        {/* renovatie-laag (enkel bij te renoveren) */}
        {rv && (
          <div className="renov">
            <div className="rh">{IC.tools}Renovatie-potentieel</div>
            <div className="rvstrip">
              <div className="rvtile"><div className="rl">Verbouwbudget</div><div className="rv">{eurk(rv.verbouwbudget)}</div></div>
              <div className="rvtile"><div className="rl">PEB-premie</div><div className="rv pos">−{eurk(rv.peb_premie)}</div></div>
              <div className="rvtile"><div className="rl">Waarde na renov.</div><div className="rv">{eurk(rv.arv)}</div></div>
              <div className="rvtile">
                <div className="rl">Marge latent</div>
                <div className={'rv ' + ((rv.marge_latent ?? 0) >= 0 ? 'pos' : 'neg')}>
                  {(rv.marge_latent ?? 0) >= 0 ? '+' : '−'}{eurk(Math.abs(rv.marge_latent ?? 0))}
                </div>
              </div>
            </div>
            <div className="rrent">
              <span className="now">Huur nu {eur(rv.huur_nu)}</span>
              <span className="ar">→</span>
              <span className="aft">na renov. {eur(rv.huur_na)}/mnd</span>
              <span className="epcj">EPC {rv.epc_nu || '—'}→{rv.epc_na || '—'}</span>
            </div>
          </div>
        )}

        {/* cashflow box */}
        <div className={'cf ' + (cfPos ? 'pos' : 'neg')}>
          <div className="ai">{cfPos ? '▲' : '▼'}</div>
          <div className="pt">Cashflow / maand<b>{cf != null && cf >= 0 ? '+' : ''}{eur(cf)}</b></div>
          {conf && <span className={'tag ' + conf}>{CONFLBL[conf] || conf}</span>}
          <div className="aipop">
            <div className="ph">Cashflow-opbouw / maand</div>
            <div className="row"><span>Huur</span><b>+{eur(m.huur_maand)}</b></div>
            <div className="row"><span>Aflossing lening</span><b>−{eur(m.aflossing_maand)}</b></div>
            <div className="row"><span>Onr. voorheffing</span><b>−{eur(m.onroerende_voorheffing_jaar != null ? m.onroerende_voorheffing_jaar / 12 : null)}</b></div>
            <div className="row"><span>Onderhoud, verzekering, leegstand</span><b>rest</b></div>
            <div className="row tot"><span>Cashflow</span><b>{cf != null && cf >= 0 ? '+' : ''}{eur(cf)}</b></div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- App */

export type ImmoAppProps = {
  keycloakToken?: string
  keycloakUser?: {
    sub: string
    preferred_username: string
    email?: string
    roles: string[]
  }
  basePath?: string
  apiBaseUrl?: string
}

export default function App({
  keycloakToken,
  keycloakUser,
  basePath,
  apiBaseUrl = DEFAULT_API,
}: ImmoAppProps) {
  void keycloakUser
  void basePath

  const api = (apiBaseUrl || DEFAULT_API).replace(/\/+$/, '')

  // The API requires a Keycloak token with the `admin` role on every data route.
  // Prefer the shell's localStorage copy (it is rewritten on every token
  // refresh) and fall back to the prop, which is the only source when the shell
  // logs in through the OIDC redirect flow rather than the direct grant.
  const authHeaders = useCallback((): HeadersInit => {
    const token = localStorage.getItem(TOKEN_STORAGE_KEY) || keycloakToken
    return token ? { Authorization: `Bearer ${token}` } : {}
  }, [keycloakToken])

  /* financiering — drives the API query params */
  const [inbreng, setInbreng] = useState(0)   // % eigen inbreng
  const [jaren, setJaren] = useState(20)
  const [rente, setRente] = useState(3.45)    // % per jaar
  const [inbrengDraft, setInbrengDraft] = useState('0')
  const [renteDraft, setRenteDraft] = useState('3,45')

  /* data */
  const [props_, setProps] = useState<Property[]>([])
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState<string | null>(null)
  const [reload, setReload] = useState(0)

  /* filters / view */
  const [sort, setSort] = useState<Sort>('koopje')
  const [regio, setRegio] = useState('')
  const [ptype, setPtype] = useState('')
  const [slk, setSlk] = useState(0)
  const [epc, setEpc] = useState('')
  const [fGarage, setFGarage] = useState(false)
  const [fGeenAsbest, setFGeenAsbest] = useState(false)
  const [fGeenFlood, setFGeenFlood] = useState(false)
  const [fVerhuurd, setFVerhuurd] = useState(false)
  const [showSold, setShowSold] = useState(false)
  const [q, setQ] = useState('')
  const [view, setView] = useState<View>('thumb')
  const [assumeOpen, setAssumeOpen] = useState(false)

  useEffect(() => {
    let alive = true
    setLoading(true)
    const qs = new URLSearchParams({
      status: 'all',
      include_sold: 'true',
      limit: '500',
      metrics: 'true',
      inbreng_pct: String(inbreng),
      jaren: String(jaren),
      rente: String(rente),
    })
    const headers = authHeaders()
    Promise.all([
      fetch(`${api}/properties?${qs.toString()}`, { headers }).then((r) => {
        if (!r.ok) throw new Error(`GET /properties → HTTP ${r.status}`)
        return r.json()
      }),
      fetch(`${api}/stats`, { headers }).then((r) => (r.ok ? r.json() : null)).catch(() => null),
    ])
      .then(([pl, st]) => {
        if (!alive) return
        setProps(Array.isArray(pl?.properties) ? (pl.properties as Property[]) : [])
        setStats((st as Stats) || null)
        setErr(null)
      })
      .catch((e: unknown) => {
        if (!alive) return
        setErr(e instanceof Error ? e.message : String(e))
      })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [api, authHeaders, inbreng, jaren, rente, reload])

  const commitInbreng = useCallback((v: string) => {
    let n = parseFloat(v.replace(',', '.'))
    if (isNaN(n) || n < 0) n = 0
    if (n > 60) n = 60
    setInbreng(n)
    setInbrengDraft(String(n))
  }, [])

  const pickInbreng = useCallback((v: number) => {
    setInbreng(v)
    setInbrengDraft(String(v))
  }, [])

  const commitRente = useCallback((v: string) => {
    let n = parseFloat(v.replace(',', '.'))
    if (isNaN(n) || n < 0) n = 0
    if (n > 15) n = 15
    setRente(n)
    setRenteDraft(n.toFixed(2).replace('.', ','))
  }, [])

  /* what the server actually computed with (falls back to the controls) */
  const aannames: AannameBlok = useMemo(() => {
    const a = props_.find((p) => p.metrics?.aannames)?.metrics?.aannames
    return a || { rente: rente / 100, jaren, inbreng_pct: inbreng / 100, ov_base: 0.0397 }
  }, [props_, rente, jaren, inbreng])

  const cities = useMemo(
    () => Array.from(new Set(props_.map((p) => p.city).filter(Boolean) as string[])).sort(),
    [props_],
  )
  const types = useMemo(
    () => Array.from(new Set(props_.map((p) => p.property_type).filter(Boolean) as string[])).sort(),
    [props_],
  )
  const sources = useMemo(
    () => Array.from(new Set(props_.map((p) => p.source_site).filter(Boolean) as string[])),
    [props_],
  )

  const passes = useCallback((p: Property) => {
    if (!showSold && isSold(p)) return false
    if (regio && p.city !== regio) return false
    if (ptype && p.property_type !== ptype) return false
    if (slk && (p.bedrooms ?? 0) < slk) return false
    if (epc) {
      const o = p.epc_label ? EPCORD[p.epc_label] : undefined
      if (o == null || o > EPCORD[epc]) return false
    }
    if (fGarage && !p.has_garage) return false
    if (fGeenAsbest && p.asbestos) return false
    if (fGeenFlood && p.flood_risk) return false
    if (fVerhuurd && !p.rented) return false
    if (q) {
      const s = [p.title, p.street, p.city, p.postal_code, p.property_type, p.source_site, p.state]
        .filter(Boolean).join(' ').toLowerCase()
      if (!s.includes(q.toLowerCase())) return false
    }
    return true
  }, [showSold, regio, ptype, slk, epc, fGarage, fGeenAsbest, fGeenFlood, fVerhuurd, q])

  const sortVal = useCallback((p: Property): number => {
    const m = p.metrics || {}
    switch (sort) {
      case 'koopje': return m.delta_vs_schatting ?? Number.POSITIVE_INFINITY
      case 'rend': return -(m.netto_rendement ?? -Infinity)
      case 'cashflow': return -(m.cashflow_maand ?? -Infinity)
      case 'irr': return -(m.irr_10j ?? -Infinity)
      case 'huur': return -(m.huur_maand ?? -Infinity)
      case 'prijs_a': return p.price ?? Number.POSITIVE_INFINITY
      case 'prijs_d': return -(p.price ?? -Infinity)
      case 'm2': return m.prijs_per_m2 ?? Number.POSITIVE_INFINITY
      case 'nieuw': return -new Date(p.first_seen || 0).getTime()
      default: return 0
    }
  }, [sort])

  const list = useMemo(
    () => props_.filter(passes).slice().sort((a, b) => sortVal(a) - sortVal(b)),
    [props_, passes, sortVal],
  )

  const actief = useMemo(() => props_.filter((p) => !isSold(p)), [props_])
  const verkocht = useMemo(() => props_.filter(isSold), [props_])

  const avgM2 = useMemo(() => {
    const v = actief.map((p) => p.metrics?.prijs_per_m2).filter((n): n is number => n != null)
    return v.length ? Math.round(v.reduce((s, n) => s + n, 0) / v.length) : null
  }, [actief])

  const best = useMemo(() => {
    let b: Property | null = null
    for (const p of actief) {
      const d = p.metrics?.delta_vs_schatting
      if (d == null) continue
      if (b == null || d < (b.metrics?.delta_vs_schatting ?? Infinity)) b = p
    }
    return b
  }, [actief])

  const lastSeen = useMemo(() => {
    const t = props_.map((p) => p.last_seen).filter(Boolean).sort()
    return t.length ? String(t[t.length - 1]).slice(0, 16).replace('T', ' ') : null
  }, [props_])

  const regions = useMemo(() => {
    const pcs = Array.from(new Set(props_.map((p) => p.postal_code).filter(Boolean) as string[]))
    return pcs.sort().join(', ')
  }, [props_])

  const inbP = Math.round(inbreng)

  return (
    <div className="immo-app">
      <style>{CSS}</style>
      <div className="layout">
        <aside className="side">
          <div className="brand">
            <div className="lg">A</div>
            <div><b>ANTON&nbsp;IMMO</b><small>Vastgoed Radar · {regions || '9100'}</small></div>
          </div>
          <nav className="nav">
            {NAV.map(([label, on]) => (
              <a key={label} className={on ? 'on' : ''}>
                <span className="ib">{IC.gridIc}</span>{label}
              </a>
            ))}
          </nav>
          <div className="sfoot">
            Live data · {api.replace(/^https?:\/\//, '')}
            <br />v{__APP_VERSION__}
          </div>
        </aside>

        <main className="main">
          <div className="top">
            <div>
              <div className="crumb">Pages / <b>Dashboard</b></div>
              <div className="h1">Vastgoed Radar <span className="ver">v{__APP_VERSION__}</span></div>
            </div>
            <div className="r">
              <div className="search">
                {IC.search}
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Zoek adres, gemeente, type…"
                />
              </div>
              <div className="seg">
                <button className={view === 'thumb' ? 'on' : ''} onClick={() => setView('thumb')}>
                  {IC.gridIc}Thumbnails
                </button>
                <button className={view === 'list' ? 'on' : ''} onClick={() => setView('list')}>
                  {IC.rows}Lijst
                </button>
              </div>
              <button
                className="iconbtn sq"
                title="Vernieuwen"
                disabled={loading}
                onClick={() => setReload((n) => n + 1)}
              >
                {IC.refresh}
              </button>
            </div>
          </div>

          {err && (
            <div className="banner">
              <b>API niet bereikbaar</b> — {err}. De radar toont wat er nog in het geheugen zit;
              probeer opnieuw te vernieuwen.
            </div>
          )}

          <div className="stats">
            <StatCard
              bg="var(--g-dark)"
              ic={icn(<><path d="M3 11.5 12 4l9 7.5" /><path d="M5 10v9h14v-9" /></>)}
              k="Actieve panden"
              v={stats?.active ?? actief.length}
              ft={<>bron: <b style={{ color: 'var(--ink)' }}>{sources.length}</b> immo-site{sources.length === 1 ? '' : 's'} · {regions || '—'}</>}
            />
            <StatCard
              bg="var(--g-green)"
              ic={icn(<><rect x="3" y="8" width="18" height="8" rx="1.5" /><line x1="7" y1="8" x2="7" y2="11" /><line x1="12" y1="8" x2="12" y2="12" /></>)}
              k="Gem. vraagprijs/m²"
              v={eur(avgM2)}
              ft="over alle actieve panden"
            />
            <StatCard
              bg="var(--g-blue)"
              ic={icn(<><polyline points="4 15 9 10 13 13 20 6" /><polyline points="15 6 20 6 20 11" /></>)}
              k="Beste koopje"
              v={spct(best?.metrics?.delta_vs_schatting)}
              ft={best ? <>{best.street || best.title} · <b>{eur(best.price)}</b></> : '—'}
            />
            <StatCard
              bg="var(--g-amber)"
              ic={icn(<><rect x="4" y="5" width="16" height="16" rx="2" /><line x1="4" y1="9" x2="20" y2="9" /><polyline points="9 14 11 16 15 12" /></>)}
              k="Verkocht-archief"
              v={stats?.sold ?? verkocht.length}
              ft="voor marktanalyse"
            />
          </div>

          {/* ---------------- financieringsbalk ---------------- */}
          <div className="finbar">
            <div className="fh">
              <span className="fi">{IC.euro}</span>
              <div>
                <b>Financiering</b>
                <small>past aflossing, cashflow &amp; IRR van elk pand live aan</small>
              </div>
            </div>
            <div className="fingrp">
              <span className="finlab">Eigen inbreng</span>
              {[0, 10, 20].map((v) => (
                <span
                  key={v}
                  className={'finchip' + (inbP === v ? ' on' : '')}
                  onClick={() => pickInbreng(v)}
                >
                  {v === 0 ? '0% · 100% lenen' : v + '%'}
                </span>
              ))}
              <input
                className="fininp"
                value={inbrengDraft}
                inputMode="numeric"
                onChange={(e) => setInbrengDraft(e.target.value)}
                onBlur={(e) => commitInbreng(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
              />
              <span className="finsuf">% inbreng</span>
            </div>
            <div className="fingrp">
              <span className="finlab">Looptijd</span>
              <div className="finseg">
                {[15, 20, 25].map((j) => (
                  <button key={j} className={jaren === j ? 'on' : ''} onClick={() => setJaren(j)}>{j}j</button>
                ))}
              </div>
            </div>
            <div className="fingrp">
              <span className="finlab">Rente</span>
              <input
                className="fininp"
                value={renteDraft}
                inputMode="decimal"
                onChange={(e) => setRenteDraft(e.target.value)}
                onBlur={(e) => commitRente(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur() }}
              />
              <span className="finsuf">%</span>
            </div>
            {loading && <span className="finbusy">herrekenen…</span>}
          </div>

          {/* ---------------- filters ---------------- */}
          <div className="filters">
            <span className="flab">Sorteer</span>
            <select className="fsel" value={sort} onChange={(e) => setSort(e.target.value as Sort)}>
              <option value="koopje">beste koopje</option>
              <option value="rend">netto rendement</option>
              <option value="cashflow">cashflow</option>
              <option value="irr">IRR 10j</option>
              <option value="huur">huuropbrengst</option>
              <option value="prijs_a">prijs ↑</option>
              <option value="prijs_d">prijs ↓</option>
              <option value="m2">€/m²</option>
              <option value="nieuw">nieuwste</option>
            </select>
            <span className="divv" />
            <span className="flab">Regio</span>
            <select className="fsel" value={regio} onChange={(e) => setRegio(e.target.value)}>
              <option value="">alle</option>
              {cities.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <select className="fsel" value={ptype} onChange={(e) => setPtype(e.target.value)}>
              <option value="">alle types</option>
              {types.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
            <select className="fsel" value={String(slk)} onChange={(e) => setSlk(Number(e.target.value))}>
              <option value="0">slaapk. — alle</option>
              <option value="2">≥ 2 slpk</option>
              <option value="3">≥ 3 slpk</option>
              <option value="4">≥ 4 slpk</option>
            </select>
            <select className="fsel" value={epc} onChange={(e) => setEpc(e.target.value)}>
              <option value="">EPC — alle</option>
              <option value="A">≥ A</option>
              <option value="B">≥ B</option>
              <option value="C">≥ C</option>
              <option value="D">≥ D</option>
            </select>
            <span className="divv" />
            <span className={'chip' + (fGarage ? ' on' : '')} onClick={() => setFGarage((v) => !v)}>
              <span className="tk" />garage
            </span>
            <span className={'chip' + (fGeenAsbest ? ' on' : '')} onClick={() => setFGeenAsbest((v) => !v)}>
              <span className="tk" />geen asbest
            </span>
            <span className={'chip' + (fGeenFlood ? ' on' : '')} onClick={() => setFGeenFlood((v) => !v)}>
              <span className="tk" />niet overstroombaar
            </span>
            <span className={'chip' + (fVerhuurd ? ' on' : '')} onClick={() => setFVerhuurd((v) => !v)}>
              <span className="tk" />verhuurd
            </span>
            <span className="divv" />
            <span className={'chip warn' + (showSold ? ' on' : '')} onClick={() => setShowSold((v) => !v)}>
              <span className="tk" />toon verkochte panden
            </span>
          </div>

          <div className="sechead">
            <h2>Panden</h2>
            <small>
              {list.length} van {actief.length} actief
              {showSold && verkocht.length > 0 ? ` (+${verkocht.length} verkocht)` : ''}
              {' — '}{SORTLBL[sort]}
            </small>
          </div>

          <div className={'grid' + (view === 'list' ? ' list' : '')}>
            {list.length > 0
              ? list.map((p) => <Card key={p.id} p={p} aannames={aannames} />)
              : (
                <div className="empty">
                  <b>{loading ? 'Panden laden…' : 'Geen panden gevonden'}</b>
                  {loading ? 'Even geduld.' : 'Pas de filters aan.'}
                </div>
              )}
          </div>

          <div className={'assume' + (assumeOpen ? ' open' : '')}>
            <h3>Reken-aannames</h3>
            <p>
              Deze constanten voeden alle berekeningen. <b>Financiering</b> (inbreng %, looptijd,
              rente) stel je live in via de balk bovenaan — de API herrekent elk pand met die
              parameters. De overige constanten zitten in de reken-engine van de backend.
            </p>
            <div className="agr">
              {[
                ['Hypotheekrente', pct(aannames.rente, 2), (aannames.jaren ?? '—') + ' jaar · instelbaar in de balk bovenaan'],
                ['Financiering', pct(1 - (aannames.inbreng_pct ?? 0), 0) + ' lening', pct(aannames.inbreng_pct, 0) + ' eigen inbreng · instelbaar bovenaan'],
                ['Registratierechten', '12%', 'Vlaanderen · niet-eigen woning (verhuur)'],
                ['Notaris + hypotheek', '~4,7%', 'ereloon + akte + inschrijving (schaal)'],
                ['OV-basistarief', pct(aannames.ov_base, 2), 'Vlaams tarief 2026'],
                ['OV-indexcoëff.', '2,30', 'KI × 2,3 × basis × (1 + opc.)'],
                ['Waardestijging', '2,5%/j', 'voor IRR-verkoop na 10 jaar'],
                ['Leegstand', '1 mnd/j', 'ingecalculeerd in cashflow'],
                ['Onderhoud', '1%/j', 'van aankoopprijs'],
                ['Referentiehuur', '€9–10,5/m²', 'per regio · × opp/staat/EPC-coëff.'],
                ['Renovatiekost', '€550/m²', 'volledige renovatie'],
                ['PEB-premie', '€70/m²', 'richtcijfer bij zwakke EPC (E/F/G)'],
              ].map(([al, av, an]) => (
                <div className="acell" key={String(al)}>
                  <div className="al">{al}</div>
                  <div className="av">{av}</div>
                  <div className="an">{an}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="foot">
            <span className="live" />
            {lastSeen ? <>Laatst gezien {lastSeen}</> : <>Nog geen scan-tijdstip</>} · crawler over{' '}
            <b>{sources.length}</b> immo-site{sources.length === 1 ? '' : 's'} · {num(props_.length)}{' '}
            panden in de radar · regio {regions || '—'}
            &nbsp;•&nbsp;
            <a onClick={() => setAssumeOpen((v) => !v)}>
              reken-aannames {assumeOpen ? 'verbergen ▴' : 'tonen ▾'}
            </a>
          </div>
        </main>
      </div>
    </div>
  )
}
